import React, { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import FeaturedProductCard from "../HomePage/FeaturedProductCard";
import { getCategories, getProducts } from "../../services/productService";
import Preloader from "../../Preloader";
import "./Style.css";
import AvailableOn from "../AvailableOn/AvailableOn";

const slugifyCategory = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const getCategorySlug = (category) =>
  category?.slug || slugifyCategory(category?.category_name || category?.category_id);

function Sweets() {
  const { categorySlug } = useParams();

  const { data: categories = [], isLoading: isCategoriesLoading } = useQuery({
    queryKey: ["product-categories"],
    queryFn: getCategories,
  });

  const { data: productResponse, isLoading: isProductsLoading } = useQuery({
    queryKey: ["product"],
    queryFn: getProducts,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const productList = useMemo(() => productResponse?.data || [], [productResponse?.data]);
  const productMap = useMemo(
    () =>
      Object.fromEntries(
        productList.map((product) => [String(product.product_id), product])
      ),
    [productList]
  );

  const activeCategories = useMemo(
    () =>
      categories
        .filter((category) => !category?.category_status || category.category_status === "Active")
        .map((category) => ({
          ...category,
          category_slug: getCategorySlug(category),
        })),
    [categories]
  );

  const categorySections = useMemo(
    () =>
      activeCategories.map((category) => {
        const mappedProducts = (category?.products || [])
          .map((categoryProduct) => productMap[String(categoryProduct.product_id)])
          .filter(Boolean);

        const fallbackProducts = productList.filter(
          (product) => String(product.category_id) === String(category.category_id)
        );

        const products = [
          ...mappedProducts,
          ...fallbackProducts.filter(
            (product) =>
              !mappedProducts.some(
                (mappedProduct) =>
                  String(mappedProduct.product_id) === String(product.product_id)
              )
          ),
        ];

        return {
          category,
          products,
        };
      }),
    [activeCategories, productList, productMap]
  );

  const selectedSlug = categorySlug || activeCategories[0]?.category_slug || "";
  const selectedSection = categorySections.find(
    ({ category }) =>
      category.category_slug === selectedSlug ||
      String(category.category_id) === String(selectedSlug)
  );
  const visibleSections = selectedSection ? [selectedSection] : [];

  const pageTitle = selectedSection?.category?.category_name
    ? `${selectedSection.category.category_name} Products`
    : "Products";

  if (isCategoriesLoading || isProductsLoading) {
    return <Preloader />;
  }

  return (
    <>
      <Helmet>
        <title>KUKUNAMKEEN | {pageTitle}</title>
        <meta
          name="description"
          content="Explore active Kuku Namkeen categories with related featured product cards."
        />
        <meta
          name="keywords"
          content="KUKUNAMKEEN, Products, Categories, Kachori, Sweets, Snacks"
        />
        <link
          rel="canonical"
          href={`https://kukunamkeen.in/categories${selectedSlug ? `/${selectedSlug}` : ""}`}
        />
      </Helmet>

      <section className="products-page">
        <section className="dist-header-title">
          <div className="max-w-6xl mx-auto px-4">
            <h1>Products</h1>
          </div>
        </section>
        <img
          src="/Image/border-bottom-title.png"
          className="w-full header-title-image"
          alt=""
        />

        <section className="products-browser">
          <div className="products-tabs-wrap">
            <div className="products-tabs" role="tablist" aria-label="Product categories">
              {activeCategories.map((category) => {
                const isActive =
                  category.category_slug === selectedSlug ||
                  String(category.category_id) === String(selectedSlug);

                return (
                  <Link
                    key={category.category_id}
                    to={`/categories/${category.category_slug}`}
                    role="tab"
                    aria-selected={isActive}
                    className={`products-tab${isActive ? " is-active" : ""}`}
                  >
                    {category.category_name}
                  </Link>
                );
              })}
            </div>
          </div>

          {visibleSections.length > 0 ? (
            visibleSections.map(({ category, products }) => (
              <div className="products-grid-wrap" key={category.category_id}>
                <div className="products-section-copy">
                  <p>{category.category_name}</p>
                </div>

                {products.length > 0 ? (
                  <div className="fp-grid products-card-grid">
                    {products.map((product) => (
                      <FeaturedProductCard
                        key={product.product_id || product.slug}
                        product={product}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="products-empty">
                    No products available in this category right now.
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="products-grid-wrap">
              <div className="products-empty">
                No category found for this product link.
              </div>
            </div>
          )}
        </section>
      </section>
      <AvailableOn />
    </>
  );
}

export default Sweets;